import React from "react";
import { ReactComponent as Developersvg } from "../images/developer_activity.svg";
import { motion } from "framer-motion";
import { SiTypescript, SiNextdotjs, SiFastapi } from "react-icons/si";

const Aboutme = () => {
  const achievements = [
    {
      icon: "",
      title: "Projects Completed",
      value: "15+",
      description: "Successfully completed and deployed multiple web applications"
    },
    {
      icon: "",
      title: "Technical Skills",
      value: "10+",
      description: "Proficient in modern web development technologies"
    },
    {
      icon: "",
      title: "Learning Goals",
      value: "Continuous",
      description: "Always learning and improving technical skills"
    }
  ];

  const currentLearning = [
    {
      icon: "📚",
      title: "Full-Stack Architecture",
      description: "Learning microservices and API design patterns"
    },
    {
      icon: "🔧",
      title: "Backend Optimization",
      description: "Improving database queries and caching strategies"
    },
    {
      icon: "🔒",
      title: "Security Best Practices",
      description: "Implementing authentication and authorization"
    },
    {
      icon: "🤖",
      title: "Machine Learning Algorithms",
      description: "Diving deep into supervised and unsupervised learning, neural networks, and real-world ML applications. Exploring how algorithms like decision trees, SVMs, and deep learning can solve complex problems."
    }
  ];

  return (
    <div className="max-w-6xl mx-auto px-2 sm:px-8 py-8 sm:py-20 select-none rounded-2xl shadow-xl bg-white flex flex-col gap-10">
      {/* Professional Tagline */}
      <section className="space-y-4 border-b pb-6">
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-2">
          About <span className="text-green-500">Me</span>
        </h1>
        <p className="text-2xl font-semibold text-gray-700 mb-2">
          Crafting meaningful digital experiences through code and creativity
        </p>
      </section>

      {/* Achievements */}
      <section className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-4 justify-center items-center border-b pb-8">
        {achievements.map((achievement, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="w-full sm:flex-1 min-w-[160px] text-center p-4 rounded-xl bg-gray-50 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300"
          >
            <div className="text-3xl mb-2 text-green-500">{achievement.icon}</div>
            <h3 className="text-lg font-semibold mb-1 text-gray-900">{achievement.value}</h3>
            <p className="text-gray-700 font-medium">{achievement.title}</p>
            <p className="text-xs text-gray-500 mt-1">{achievement.description}</p>
          </motion.div>
        ))}
      </section>

      {/* My Story (Shortened) */}
      <section className="space-y-2 border-b pb-8">
        <h2 className="text-xl font-semibold mb-2 text-green-600">My Story</h2>
        <p className="text-base text-gray-700 leading-relaxed">
          Hi, I'm <span className="font-bold text-green-600 hover:text-green-800 cursor-pointer">Sumit Bhusal</span>, a full-stack developer passionate about building robust and scalable web applications. I love connecting frontend and backend to deliver seamless user experiences.
        </p>
        <p className="text-base text-gray-600 leading-relaxed">
          My journey started with frontend technologies and quickly expanded to backend development. I enjoy solving real-world problems with clean, efficient code.
        </p>
      </section>

      {/* What I'm Currently Learning (Shortened) */}
      <section className="space-y-2 border-b pb-8">
        <h2 className="text-xl font-semibold mb-2 text-green-600">What I'm Currently Learning</h2>
        <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-4">
          {[
            {
              icon: "📚",
              title: "Full-Stack Architecture",
              description: "Learning scalable app patterns, microservices, and API design."
            },
            {
              icon: "🔧",
              title: "DevOps",
              description: "Docker containerization, CI/CD pipelines, cloud deployment strategies."
            },
            {
              icon: "🔒",
              title: "Security Best Practices",
              description: "Implementing secure authentication and protecting APIs."
            },
            {
              icon: "🤖",
              title: "Machine Learning",
              description: "Exploring ML basics, neural networks, and real-world use cases."
            }
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="w-full sm:flex-1 min-w-[160px] p-4 rounded-xl bg-gray-50 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 text-center"
            >
              <div className="text-2xl mb-2 text-green-500">{item.icon}</div>
              <h3 className="text-base font-semibold mb-1 text-gray-900">{item.title}</h3>
              <p className="text-gray-700 text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Favorite Quote in Code Format */}
      <section className="mt-8 border-b pb-8">
        <h2 className="text-xl font-semibold mb-2 text-green-600">Favorite Quote</h2>
        <pre className="bg-gray-900 text-green-400 rounded-lg p-4 text-sm overflow-x-auto font-mono shadow-md">
{`// The only way to do great work is to love what you do.
// - Steve Jobs

const keepLearning = () => {
  while (true) {
    learn();
    build();
    share();
  }
};`}
        </pre>
      </section>

      {/* Skills Progress Section */}
      <section className="mt-8">
        <h2 className="text-xl font-semibold mb-2 text-green-600">Skills Progress</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
          {[
            { name: 'Django', percent: 80 },
            { name: 'FastAPI', percent: 75 },
            { name: 'Python', percent: 80 },
            { name: 'TypeScript', percent: 85 },
            { name: 'React', percent: 85 },
            { name: 'Next.js', percent: 80 },
            { name: 'Tailwind CSS', percent: 80 },
            { name: 'JavaScript', percent: 85 },
            { name: 'HTML', percent: 90 },
            { name: 'CSS', percent: 88 },
            { name: 'PostgreSQL', percent: 75 },
            { name: 'Node.js', percent: 70 },
            { name: 'Bootstrap', percent: 65 },
            { name: 'Git', percent: 85 },
            { name: 'GitHub', percent: 80 },
          ].map(skill => (
            <div key={skill.name} className="bg-white rounded-lg shadow p-4 flex flex-col justify-between">
              <div className="flex justify-between mb-2">
                <span className="text-gray-800 font-medium">{skill.name}</span>
                <span className="text-gray-600 text-sm">{skill.percent}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div
                  className="bg-gradient-to-r from-blue-400 to-blue-700 h-3 rounded-full transition-all duration-700"
                  style={{ width: `${skill.percent}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Aboutme;
